import moment, { Moment } from "moment";
import { SensorType } from "../Models/sensor";
import { DeviceModel, DeviceType } from "../Models/devices";
import { RoleModel } from "../Models/roles";
import { CustomNotificationType } from "../Models/notifications";
import { LicenseCountersModel } from "../Models/license";

const END_OF_DAY_HOURS = 23;
const END_OF_DAY_MINUTES = 59;
const END_OF_DAY_SECONDS = 59;
const ISO_TIMEZONE_OFFSET_REGEX = /[+-]\d{2}:?\d{2}$/;

export const hasRole = (user: any, roles: any) =>
  roles.some((role: any) => user.roles.includes(role));

export function updateObject(oldObject: any, updatedProperties: any) {
  return {
    ...oldObject,
    ...updatedProperties,
  };
}

export const fiteToBase64 = (file: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export function createTimeDuration(startTime: Moment, endTime: Moment) {
  const duration = moment.duration(startTime.diff(endTime));
  return duration.asMinutes();
}

export function cleanIPaddress(address: string) {
  const subips = address.split(".");
  const result =
    parseInt(subips[0]) +
    "." +
    parseInt(subips[1]) +
    "." +
    parseInt(subips[2]) +
    "." +
    parseInt(subips[3]);

  return result;
}

export function convertSensorTypeToString(actor: SensorType): string {
  switch (actor) {
    case SensorType.Contact:
      return "Contact";
    case SensorType.Motion:
      return "Motion";
    case SensorType.Relay:
      return "WirelessRelay";
    case SensorType.PushButton:
      return "PushPanicExitButton";
    case SensorType.LightSwitch:
      return "LightSwitch";
    case SensorType.Alarm:
      return "SirenAlarm";
    default:
      return "None";
  }
}

export const isModelNextSensor = (model: string): boolean => model === "VNX-SN-VRS1-0";

export const filterDevicesByType = (
  devices: DeviceModel[],
  deviceType: DeviceType
): DeviceModel[] => devices.filter((d) => d && d.deviceType === deviceType);

export function isRoleModel(option: any): option is RoleModel {
  if (!("displayName" in option) || typeof option["displayName"] !== "string") {
    return false;
  }
  if (!("id" in option) || typeof option["id"] !== "number") {
    return false;
  }
  if (!("isAdmin" in option) || typeof option["isAdmin"] !== "boolean") {
    return false;
  }
  return true;
}

export function isCustomNotificationType(value: any): value is CustomNotificationType {
  return typeof value === "number" && Object.values(CustomNotificationType).includes(value);
}

export function getCountValue(key: keyof LicenseCountersModel, data: LicenseCountersModel): number {
  return data[key] ?? 0;
}

export function formatUtcToLocal(utcIsoString: string): string {
  if (!utcIsoString) {
    return "N/A";
  }
  const date = new Date(utcIsoString);
  if (isNaN(date.getTime())) {
    return "Invalid date";
  }
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

export function appendUtcSuffixIfMissing(isoString: string): string {
  if (!isoString) {
    return "";
  }
  if (isoString.endsWith("Z") || ISO_TIMEZONE_OFFSET_REGEX.test(isoString)) {
    return isoString;
  }
  return isoString + "Z";
}

export function toEndOfDayUtcIso(momentDate: import("moment").Moment): string {
  const endOfDayUtc = new Date(
    Date.UTC(
      momentDate.year(),
      momentDate.month(),
      momentDate.date(),
      END_OF_DAY_HOURS,
      END_OF_DAY_MINUTES,
      END_OF_DAY_SECONDS,
      0
    )
  );
  return endOfDayUtc.toISOString();
}
